import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import {
    FleetAvailabilityResponse,
    FleetAvailabilityByCompanyGroupSearchParams,
    FleetAvailabilitySearchParams,
    Mobilization,
    MobilizationAuditEvent,
    MobilizationDashboard,
    MobilizationDashboardSearchParams,
    MobilizationDailyPlanning,
    MobilizationDailyPlanningSearchParams,
    MobilizationRoadTrackingRow,
    MobilizationRoadTrackingSearchParams,
    MobilizationSearchParams,
    MobilizationSalaryRule,
    MobilizationSalaryRuleSearchParams,
    MobilizationStatusAction
} from './models/mobilization.model';

@Injectable({
    providedIn: 'root'
})
export class MobilizationService {
    private readonly baseUrl = `${environment.restApiUrl}fleet-mobilization-endpoint`;

    constructor(private http: HttpClient) {}

    searchMobilizations(filters: MobilizationSearchParams): Observable<APIListWrapper<Mobilization>> {
        let params = new HttpParams()
            .set('owningCompanyId', filters.owningCompanyId.toString())
            .set('page', (filters.page ?? 0).toString())
            .set('pageSize', (filters.pageSize ?? 20).toString());

        params = this.appendIfPresent(params, 'status', filters.status);
        params = this.appendIfPresent(params, 'keyword', filters.keyword);
        params = this.appendIfPresent(params, 'driverId', filters.driverId);
        params = this.appendIfPresent(params, 'vehicleId', filters.vehicleId);
        params = this.appendIfPresent(params, 'logisticsProjectId', filters.logisticsProjectId);
        params = this.appendIfPresent(params, 'startFrom', this.toDateParam(filters.startFrom));
        params = this.appendIfPresent(params, 'startTo', this.toDateParam(filters.startTo));
        params = this.appendIfPresent(params, 'overlapFrom', this.toDateParam(filters.overlapFrom));
        params = this.appendIfPresent(params, 'overlapTo', this.toDateParam(filters.overlapTo));
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'trailerId', filters.trailerId);
        params = this.appendIfPresent(params, 'trailerTypeId', filters.trailerTypeId);

        return this.http.get<APIListWrapper<Mobilization>>(`${this.baseUrl}/mobilizations`, { params });
    }

    getDashboard(filters: MobilizationDashboardSearchParams): Observable<APIResponseWrapper<MobilizationDashboard>> {
        let params = new HttpParams().set('owningCompanyId', filters.owningCompanyId.toString());
        params = this.appendIfPresent(params, 'dateFrom', this.toDateParam(filters.dateFrom));
        params = this.appendIfPresent(params, 'dateTo', this.toDateParam(filters.dateTo));
        params = this.appendIfPresent(params, 'status', filters.status);
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'trailerTypeId', filters.trailerTypeId);
        params = this.appendIfPresent(params, 'logisticsProjectId', filters.logisticsProjectId);

        return this.http.get<APIResponseWrapper<MobilizationDashboard>>(`${this.baseUrl}/dashboard`, { params });
    }

    getDailyPlanning(filters: MobilizationDailyPlanningSearchParams): Observable<APIResponseWrapper<MobilizationDailyPlanning>> {
        let params = new HttpParams().set('owningCompanyId', filters.owningCompanyId.toString());

        params = this.appendIfPresent(params, 'dateFrom', this.toDateParam(filters.dateFrom));
        params = this.appendIfPresent(params, 'dateTo', this.toDateParam(filters.dateTo));
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'destinationLocationId', filters.destinationLocationId);
        params = this.appendIfPresent(params, 'destination', filters.destination);
        params = this.appendIfPresent(params, 'includeOpenReleases', filters.includeOpenReleases);
        params = this.appendIfPresent(params, 'includeTracking', filters.includeTracking);

        return this.http.get<APIResponseWrapper<MobilizationDailyPlanning>>(`${this.baseUrl}/daily-planning`, { params });
    }

    getRoadTracking(filters: MobilizationRoadTrackingSearchParams): Observable<APIResponseWrapper<MobilizationRoadTrackingRow[]>> {
        let params = new HttpParams().set('owningCompanyId', filters.owningCompanyId.toString());

        params = this.appendIfPresent(params, 'dateFrom', this.toDateParam(filters.dateFrom));
        params = this.appendIfPresent(params, 'dateTo', this.toDateParam(filters.dateTo));
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'destinationLocationId', filters.destinationLocationId);
        params = this.appendIfPresent(params, 'destination', filters.destination);
        params = this.appendIfPresent(params, 'jobStatus', filters.jobStatus);

        return this.http.get<APIResponseWrapper<MobilizationRoadTrackingRow[]>>(`${this.baseUrl}/road-tracking`, { params });
    }

    getAvailability(filters: FleetAvailabilitySearchParams): Observable<APIResponseWrapper<FleetAvailabilityResponse>> {
        let params = new HttpParams().set('owningCompanyId', filters.owningCompanyId.toString());

        params = this.appendIfPresent(params, 'dateFrom', this.toDateParam(filters.dateFrom));
        params = this.appendIfPresent(params, 'dateTo', this.toDateParam(filters.dateTo));
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'driverId', filters.driverId);
        params = this.appendIfPresent(params, 'vehicleId', filters.vehicleId);
        params = this.appendIfPresent(params, 'status', filters.status);
        params = this.appendIfPresent(params, 'ownershipType', filters.ownershipType);

        return this.http.get<APIResponseWrapper<FleetAvailabilityResponse>>(`${this.baseUrl}/availability`, { params });
    }

    getAvailabilityByCompanyGroup(filters: FleetAvailabilityByCompanyGroupSearchParams): Observable<APIResponseWrapper<FleetAvailabilityResponse>> {
        let params = new HttpParams()
            .set('companyGroupId', filters.companyGroupId.toString())
            .set('excludeCompanyId', filters.excludeCompanyId.toString());

        params = this.appendIfPresent(params, 'memberCompanyId', filters.memberCompanyId);
        params = this.appendIfPresent(params, 'dateFrom', this.toDateParam(filters.dateFrom));
        params = this.appendIfPresent(params, 'dateTo', this.toDateParam(filters.dateTo));
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'driverId', filters.driverId);
        params = this.appendIfPresent(params, 'vehicleId', filters.vehicleId);
        params = this.appendIfPresent(params, 'status', filters.status);
        params = this.appendIfPresent(params, 'ownershipType', filters.ownershipType);

        return this.http.get<APIResponseWrapper<FleetAvailabilityResponse>>(`${this.baseUrl}/availability-by-company-group`, { params });
    }

    searchSalaryRules(filters: MobilizationSalaryRuleSearchParams): Observable<APIListWrapper<MobilizationSalaryRule>> {
        let params = new HttpParams()
            .set('owningCompanyId', filters.owningCompanyId.toString())
            .set('page', (filters.page ?? 0).toString())
            .set('pageSize', (filters.pageSize ?? 20).toString());

        params = this.appendIfPresent(params, 'active', filters.active);
        params = this.appendIfPresent(params, 'vehicleTypeId', filters.vehicleTypeId);
        params = this.appendIfPresent(params, 'keyword', filters.keyword);

        return this.http.get<APIListWrapper<MobilizationSalaryRule>>(`${this.baseUrl}/salary-rules`, { params });
    }

    getSalaryRule(id: number): Observable<APIResponseWrapper<MobilizationSalaryRule>> {
        return this.http.get<APIResponseWrapper<MobilizationSalaryRule>>(`${this.baseUrl}/salary-rules/${id}`);
    }

    createSalaryRule(rule: MobilizationSalaryRule): Observable<APIResponseWrapper<MobilizationSalaryRule>> {
        return this.http.post<APIResponseWrapper<MobilizationSalaryRule>>(`${this.baseUrl}/salary-rules`, this.normalizeSalaryRuleDates(rule));
    }

    updateSalaryRule(id: number, rule: MobilizationSalaryRule): Observable<APIResponseWrapper<MobilizationSalaryRule>> {
        return this.http.put<APIResponseWrapper<MobilizationSalaryRule>>(`${this.baseUrl}/salary-rules/${id}`, this.normalizeSalaryRuleDates(rule));
    }

    updateSalaryRuleActive(id: number, active: boolean): Observable<APIResponseWrapper<MobilizationSalaryRule>> {
        const params = new HttpParams().set('active', active.toString());
        return this.http.patch<APIResponseWrapper<MobilizationSalaryRule>>(`${this.baseUrl}/salary-rules/${id}/active`, null, { params });
    }

    getMobilization(id: number): Observable<APIResponseWrapper<Mobilization>> {
        return this.http.get<APIResponseWrapper<Mobilization>>(`${this.baseUrl}/mobilizations/${id}`);
    }

    getAuditEvents(owningCompanyId: number): Observable<APIResponseWrapper<MobilizationAuditEvent[]>> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<APIResponseWrapper<MobilizationAuditEvent[]>>(`${this.baseUrl}/audit-events`, { params });
    }

    createMobilization(mobilization: Mobilization): Observable<APIResponseWrapper<Mobilization>> {
        return this.http.post<APIResponseWrapper<Mobilization>>(`${this.baseUrl}/mobilizations`, this.normalizeDates(mobilization));
    }

    updateMobilization(id: number, mobilization: Mobilization): Observable<APIResponseWrapper<Mobilization>> {
        return this.http.put<APIResponseWrapper<Mobilization>>(`${this.baseUrl}/mobilizations/${id}`, this.normalizeDates(mobilization));
    }

    submit(id: number, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.postStatus(id, 'submit', action);
    }

    approve(id: number, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.postStatus(id, 'approve', action);
    }

    reject(id: number, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.postStatus(id, 'reject', action);
    }

    activate(id: number, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.postStatus(id, 'activate', action);
    }

    complete(id: number, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.postStatus(id, 'complete', action);
    }

    cancel(id: number, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.postStatus(id, 'cancel', action);
    }

    recalculateSalary(id: number): Observable<APIResponseWrapper<Mobilization>> {
        return this.http.post<APIResponseWrapper<Mobilization>>(`${this.baseUrl}/mobilizations/${id}/salary/recalculate`, {});
    }

    private postStatus(id: number, actionName: string, action?: MobilizationStatusAction): Observable<APIResponseWrapper<Mobilization>> {
        return this.http.post<APIResponseWrapper<Mobilization>>(`${this.baseUrl}/mobilizations/${id}/${actionName}`, action ?? {});
    }

    private appendIfPresent(params: HttpParams, key: string, value: unknown): HttpParams {
        if (value === null || value === undefined || value === '') {
            return params;
        }
        return params.set(key, value.toString());
    }

    private normalizeDates(mobilization: Mobilization): Mobilization {
        return {
            ...mobilization,
            startDate: this.toDateParam(mobilization.startDate),
            endDate: this.toDateParam(mobilization.endDate)
        };
    }

    private normalizeSalaryRuleDates(rule: MobilizationSalaryRule): MobilizationSalaryRule {
        return {
            ...rule,
            effectiveFrom: this.toDateParam(rule.effectiveFrom),
            effectiveTo: this.toDateParam(rule.effectiveTo)
        };
    }

    private toDateParam(value?: string | Date): string | undefined {
        if (!value) {
            return undefined;
        }
        if (value instanceof Date) {
            return value.toISOString().slice(0, 10);
        }
        return value.toString().slice(0, 10);
    }
}
